import React, { useEffect, useState, useMemo } from "react";
import { Button } from "../../components/ui/button";
import { CirclePlus, DeleteIcon, Edit, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Separator } from "../../components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Select as AntdSelect } from "antd";

const Subadmins = () => {
  const token = localStorage.getItem("adminToken");
  const [admins, setAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  console.log("admins : ",admins)
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });

  const [updateFormData, setUpdateFormData] = useState({
    name: "",
    email: "",
    password: "",
  });

  const modules = [
    "dashboard",
    "subadmin",
    "banner",
    "categories",
    "subCategories",
    "services",
    "subServices",
    "offers",
    "productInventory",
    "orders",
    "booking",
    "refundRequest",
    "reviews",
    "customer",
    "promotionalVideo",
    "providerVerification",
    "verifiedProvider",
    "enquiry",
    "sales",
    "revenue",
    "register",
  ];

  const Options = modules.map((module) => ({
    value: module,
    label: module,
  }));

  const [permissions, setPermissions] = useState(
    modules.reduce((acc, module) => {
      acc[module] = false;
      return acc;
    }, {})
  );

  // Filter admins based on search term
  const filteredAdmins = useMemo(() => {
    if (!searchTerm) return admins;
    
    const lowercasedSearch = searchTerm.toLowerCase();
    return admins.filter(admin => 
      admin.name?.toLowerCase().includes(lowercasedSearch) ||
      admin.email?.toLowerCase().includes(lowercasedSearch) ||
      Object.keys(admin.permissions).some(module => 
        module.toLowerCase().includes(lowercasedSearch) && admin.permissions[module]
      )
    );
  }, [admins, searchTerm]);

  const fetchAdmins = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get("https://wavetechservices.in/api/admin/profiles", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setAdmins(res.data.admins);
    } catch (error) {
      console.error("Error fetching admins:", error);
      toast.error("Failed to fetch admins");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleUpdateChange = (e) => {
    const { name, value } = e.target;
    setUpdateFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handlePermissionChange = (selectedModules) => {
    const updatedPermissions = modules.reduce((acc, module) => {
      acc[module] = selectedModules.includes(module);
      return acc;
    }, {});
    setPermissions(updatedPermissions);
  };

  const handleUpload = async () => {
    try {
      await axios.post(
        "https://wavetechservices.in/api/admin/create",
        {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          permissions,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("Admin added successfully!");
      setOpenAdd(false);
      fetchAdmins();
      setFormData({
        name: "",
        email: "",
        password: "",
      });
      setPermissions(modules.reduce((acc, module) => {
        acc[module] = false;
        return acc;
      }, {}));
    } catch (error) {
      console.error("Upload Error:", error);
      toast.error(error.response?.data?.message || "Failed to add admin");
    }
  };

  const handleUpdate = async () => {
    if (!selectedAdmin) return;
    setIsLoading(true);
    console.log("permissions : " , permissions)
    // return 
    try {
      await axios.put(
        `https://wavetechservices.in/api/admin/profile/${selectedAdmin._id}`,
        {
          name: updateFormData.name,
          email: updateFormData.email,
          password: updateFormData.password,
          permissions,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("SubAdmin updated successfully!");
      setOpenEdit(false);
      fetchAdmins();
    } catch (error) {
      console.error("Upload Error:", error);
      toast.error("SubAdmin update failed!");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedAdmin) return;
    setIsLoading(true);

    try {
      await axios.delete(
        `https://wavetechservices.in/api/admin/profile/${selectedAdmin._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("SubAdmin deleted successfully!");
      setOpenDelete(false);
      fetchAdmins();
    } catch (err) {
      console.error(err, "error");
      toast.error("SubAdmin deletion failed!");
    } finally {
      setIsLoading(false);
    }
  };

  // Open edit dialog with admin data
  const openEditDialog = (admin) => {
    setSelectedAdmin(admin);
    setUpdateFormData({
      name: admin.name,
      email: admin.email,
      password: "",
    });
    setPermissions(admin.permissions);
    setOpenEdit(true);
  };

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Calculate pagination based on filtered admins
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAdmins.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAdmins.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="px-[1vw] my-5">
      <Toaster />
      <div className="flex lg:justify-between lg:items-center flex-col lg:flex-row">
        <h1 className="text-2xl">SubAdmins ({filteredAdmins.length})</h1>
        <div className="flex lg:gap-4 items-center gap-2 my-3 lg:my-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search subadmins..."
              className="pl-10 w-full lg:w-64"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Dialog open={openAdd} onOpenChange={setOpenAdd}>
            <DialogTrigger>
              <Button className="bg-black px-5 text-white h-9 hover:opacity-90">
                Add <CirclePlus className="size-4 ml-1" />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle className="lg:text-2xl text-xl">
                  Add SubAdmin
                </DialogTitle>
              </DialogHeader>
              <div className="flex flex-col py-4 space-y-3">
                <div className="flex items-center gap-4">
                  <Label htmlFor="name" className="w-44">
                    Name
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    name="name"
                    onChange={handleChange}
                    value={formData.name}
                    className="col-span-3"
                    required
                  />
                </div>
                <div className="flex items-center gap-4">
                  <Label htmlFor="email" className="w-44">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    onChange={handleChange}
                    value={formData.email}
                    className="col-span-3"
                    required
                  />
                </div>
                <div className="flex items-center gap-4">
                  <Label htmlFor="password" className="w-44">
                    Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    name="password"
                    onChange={handleChange}
                    value={formData.password}
                    className="col-span-3"
                    required
                  />
                </div>
                <div className="flex items-center gap-4">
                  <Label htmlFor="permissions" className="w-44">
                    Permissions
                  </Label>
                  <div className="h-full w-full">
                    <AntdSelect
                      mode="multiple"
                      allowClear
                      className="h-full w-full"
                      placeholder="Select Modules"
                      options={Options}
                      onChange={handlePermissionChange}
                      value={modules.filter(module => permissions[module])}
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-center items-center">
                <Button
                  type="submit"
                  className="bg-black px-5 text-white h-9 hover:opacity-90"
                  onClick={handleUpload}
                  disabled={!formData.name || !formData.email || !formData.password}
                >
                  Add
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <Separator className="my-4" />

      <div className="overflow-x-auto lg:max-w-full h-[65vh]">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <p>Loading subadmins...</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">Sl No.</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Modules assigned</TableHead>
                <TableHead className="w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentItems.length > 0 ? (
                currentItems.map((admin, index) => (
                  <TableRow key={admin._id}>
                    <TableCell>{indexOfFirstItem + index + 1}.</TableCell>
                    <TableCell className="font-medium">{admin.name}</TableCell>
                    <TableCell className="truncate max-w-[200px]">{admin.email}</TableCell>
                    <TableCell>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="sm">
                            View Modules
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Assigned Modules</DialogTitle>
                            <DialogDescription>
                              <ul className="space-y-2">
                                {Object.entries(admin.permissions).map(([module, access]) => (
                                  <li key={module} className="flex items-center gap-2">
                                    <span className="capitalize">{module.replace(/([A-Z])/g, ' $1').trim()}</span>
                                    {access ? (
                                      <span className="text-green-500">✔️</span>
                                    ) : (
                                      <span className="text-red-500">❌</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </DialogDescription>
                          </DialogHeader>
                        </DialogContent>
                      </Dialog>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(admin)}
                          disabled={isLoading}
                        >
                          <Edit className="text-blue-500 size-4" />
                        </Button>
                       {admin.name === "Wave Admin" ? <></> :  <Dialog open={openDelete} onOpenChange={setOpenDelete}>
                          <DialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setSelectedAdmin(admin)}
                              disabled={isLoading}
                            >
                              <DeleteIcon className="text-red-500 size-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Confirm Deletion</DialogTitle>
                              <DialogDescription>
                                Are you sure you want to delete the subadmin "{selectedAdmin?.name}"?
                                This action cannot be undone.
                              </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                              <Button
                                variant="destructive"
                                onClick={handleDelete}
                                disabled={isLoading}
                              >
                                {isLoading ? "Deleting..." : "Delete"}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    {searchTerm ? "No matching subadmins found" : "No subadmins available"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Edit Subadmin Dialog */}
      <Dialog open={openEdit} onOpenChange={setOpenEdit}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="lg:text-2xl text-xl">
              Edit SubAdmin
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col py-4 space-y-3">
            <div className="flex items-center gap-4">
              <Label htmlFor="edit-name" className="w-44">
                Name
              </Label>
              <Input
                id="edit-name"
                type="text"
                name="name"
                onChange={handleUpdateChange}
                value={updateFormData.name}
                className="col-span-3"
                required
              />
            </div>
            <div className="flex items-center gap-4">
              <Label htmlFor="edit-email" className="w-44">
                Email
              </Label>
              <Input
                id="edit-email"
                type="email"
                name="email"
                onChange={handleUpdateChange}
                value={updateFormData.email}
                className="col-span-3"
                required
              />
            </div>
            <div className="flex items-center gap-4">
              <Label htmlFor="edit-password" className="w-44">
                Password (leave blank to keep current)
              </Label>
              <Input
                id="edit-password"
                type="password"
                name="password"
                onChange={handleUpdateChange}
                value={updateFormData.password}
                className="col-span-3"
              />
            </div>
            <div className="flex items-center gap-4">
              <Label htmlFor="edit-permissions" className="w-44">
                Permissions
              </Label>
              <div className="h-full w-full">
                <AntdSelect
                  mode="multiple"
                  allowClear
                  className="h-full w-full"
                  placeholder="Select Modules"
                  options={Options}
                  onChange={handlePermissionChange}
                  value={modules.filter(module => permissions[module])}
                />
              </div>
            </div>
          </div>
          <div className="flex justify-center items-center">
            <Button
              type="submit"
              className="bg-black px-5 text-white h-9 hover:opacity-90"
              onClick={handleUpdate}
              disabled={!updateFormData.name || !updateFormData.email || isLoading}
            >
              {isLoading ? "Updating..." : "Update"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {filteredAdmins.length > itemsPerPage && (
        <div className="mt-4">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                />
              </PaginationItem>

              {Array.from({ length: totalPages }).map((_, index) => {
                const page = index + 1;
                if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                  return (
                    <PaginationItem key={page}>
                      <PaginationLink
                        isActive={page === currentPage}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                }
                return null;
              })}

              <PaginationItem>
                <PaginationNext 
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default Subadmins;